'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import Image from 'next/image';
import Link from 'next/link';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { ButtonLink } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';
import { AffiliateId } from '@/models';

import { FAQ_DATA, FaqEntry } from './faq-data';
import { LineChart } from './LineChart';
import { NATIONAL, SIGNAL, StatTriple, SUBGROUP_DATA, TICKER, YOY } from './trust-signal-data';
import './trust-signal.css';

const GENERATIONS = ['Gen Z', 'Millennial', 'Gen X', 'Baby Boomer'] as const;
const REGIONS = ['Northeast', 'Midwest', 'South', 'West'] as const;

type Generation = (typeof GENERATIONS)[number];
type Region = (typeof REGIONS)[number];

const LOADING_MSGS = ['Finding homeowners like you…', 'Reading 1,112 responses…', 'Pulling your trust picture…'];

// External absolute URL (Aurora corporate site, not the marketplace domain the ButtonLink pattern handles).
const WWW_URL = process.env.NEXT_PUBLIC_WWW_URL ?? 'https://aurorasolar.com';
const SNAPSHOT_URL = `${WWW_URL}/aurora-solar-snapshot/`;

// In-app relative blog paths — no target="_blank", no full page loads.
const BLOG_LINKS = {
  costBenefits: '/home-solar/blog/solar-101/the-economic-benefits-of-solar-for-homeowners',
  goodFitForSolar: '/home-solar/blog/solar-101/is-my-house-good-for-solar',
  scamsToAvoid: '/home-solar/blog/solar-101/solar-scams-to-avoid',
};

type BestDealCard = {
  body: string;
  href: string;
  icon: DS.IIconComponent;
  linkText: string;
  supIndex: 1 | 2;
  title: string;
};

const BEST_DEAL_CARDS: ReadonlyArray<BestDealCard> = [
  {
    body: 'The single biggest trust-builder: a realistic install cost and a clear answer on when savings actually begin. 57% of homeowners say that’s what earns their confidence.',
    href: BLOG_LINKS.costBenefits,
    icon: DS.IconMoney,
    linkText: 'Cost & benefits explained',
    supIndex: 2,
    title: 'Straight talk on cost',
  },
  {
    body: 'When choosing an installer, homeowners look hardest at two things: reviews and testimonials (54%) and technical expertise and certifications (54%).',
    href: BLOG_LINKS.scamsToAvoid,
    icon: DS.IconStar,
    linkText: 'Choosing an installer you can trust',
    supIndex: 1,
    title: 'Proof from real people',
  },
  {
    body: 'Homeowners say clearer costs, reliable performance data, and more transparency would raise their confidence most.',
    href: BLOG_LINKS.goodFitForSolar,
    icon: DS.IconVisible,
    linkText: 'Is my house a good fit for solar?',
    supIndex: 2,
    title: 'Transparency',
  },
];

const renderFaqAnswer = ({ a, segments }: FaqEntry): ReactNode => {
  if (!segments) return a;
  return (
    <>
      {segments.map((seg) =>
        seg.type === 'link' ? (
          <a className="faq-link" href={seg.href} key={`link:${seg.href}`} rel="noopener noreferrer" target="_blank">
            {seg.text}
          </a>
        ) : (
          <span key={`text:${seg.text}`}>{seg.text}</span>
        )
      )}
    </>
  );
};

/* =================== TOOL (Module 3) =================== */

type MatchTier = 'exact' | 'closest' | 'national';
type Match = { data: StatTriple; phrase: string; tier: MatchTier };

// When a generation is too small to publish (Gen Z, n<30 in Snapshot 2026), fall back to the
// nearest generation we do have data for and re-enter the region lookup — so Gen Z + Midwest
// resolves to Millennial|Midwest (not Millennial national).
const GEN_FALLBACK: Partial<Record<Generation, Generation>> = { 'Gen Z': 'Millennial' };

const resolveMatch = (gen: Generation, region: Region): Match => {
  const exactKey = `${gen}|${region}`;
  if (SUBGROUP_DATA[exactKey]) {
    return { data: SUBGROUP_DATA[exactKey], phrase: `${gen} homeowners in the ${region}`, tier: 'exact' };
  }
  if (SUBGROUP_DATA[gen]) {
    return { data: SUBGROUP_DATA[gen], phrase: `${gen} homeowners`, tier: 'closest' };
  }
  const fallbackGen = GEN_FALLBACK[gen];
  if (fallbackGen) {
    const fallbackRegionKey = `${fallbackGen}|${region}`;
    if (SUBGROUP_DATA[fallbackRegionKey]) {
      return {
        data: SUBGROUP_DATA[fallbackRegionKey],
        phrase: `${fallbackGen} homeowners in the ${region} (closest available match for ${gen})`,
        tier: 'closest',
      };
    }
    if (SUBGROUP_DATA[fallbackGen]) {
      return {
        data: SUBGROUP_DATA[fallbackGen],
        phrase: `${fallbackGen} homeowners (closest available match for ${gen})`,
        tier: 'closest',
      };
    }
  }
  return { data: NATIONAL, phrase: 'homeowners nationwide', tier: 'national' };
};

const MATCH_LABEL: Record<MatchTier, { cls: string; text: string }> = {
  closest: { cls: 'closest', text: 'Closest match' },
  exact: { cls: 'exact', text: 'Exact match' },
  national: { cls: 'national', text: 'National average' },
};

const Loading = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % LOADING_MSGS.length), 900);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="ts-card">
      <div className="ts-loading">
        <div className="ts-spinner" />
        <div className="ts-loadmsg">{LOADING_MSGS[i]}</div>
      </div>
    </div>
  );
};

type StatCardProps = {
  aside: string;
  desc: string;
  figure: number;
  kicker: string;
};

const StatCard = ({ aside, desc, figure, kicker }: StatCardProps) => (
  <div className="ts-stat">
    <p className="kicker">{kicker}</p>
    <div className="figure">
      <span>{figure}</span>
      <span className="unit">%</span>
    </div>
    <p className="desc">{desc}</p>
    <p className="editorial">{aside}</p>
  </div>
);

type ToolStep = 'intro' | 'select' | 'loading' | 'results';

const ToolResults = ({ gen, onRestart, region }: { gen: Generation; onRestart: () => void; region: Region }) => {
  const { data, phrase, tier } = resolveMatch(gen, region);
  const label = MATCH_LABEL[tier];
  return (
    <div className="ts-card">
      <p className="ts-eyebrow">
        <span className="dot" />
        <span>Your trust picture</span>
      </p>
      <p className="ts-match">
        <span>Here&rsquo;s the trust picture for </span>
        <strong>{phrase}</strong>
        <span>.</span>
      </p>
      <span className={`ts-pill ${label.cls}`}>
        <span className="dot" />
        <span>{label.text}</span>
      </span>
      <StatCard
        aside="Trust is the hesitation, not price, not panels."
        desc="name trustworthiness of solar companies as a concern."
        figure={data.stat1}
        kicker="The worry"
      />
      <StatCard
        aside="When trust isn’t resolved, people simply stall."
        desc="say “I’m not sure which installers to trust” is holding them back."
        figure={data.stat3}
        kicker="The barrier"
      />
      <StatCard
        aside="This is what earns trust: proof from real people."
        desc="look to reviews and testimonials when choosing an installer."
        figure={data.stat2}
        kicker="The solution"
      />

      <DS.Button icon={DS.IconArrowLeft} mt={5} onClick={onRestart} variant="text">
        Start over
      </DS.Button>
    </div>
  );
};

const Tool = () => {
  const [step, setStep] = useState<ToolStep>('intro');
  const [gen, setGen] = useState<Generation | null>(null);
  const [region, setRegion] = useState<Region | null>(null);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    },
    []
  );

  const run = () => {
    setStep('loading');
    if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    loadingTimerRef.current = setTimeout(() => setStep('results'), 2300);
  };

  const restart = () => {
    if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    setGen(null);
    setRegion(null);
    setStep('intro');
  };

  const ready = gen !== null && region !== null;

  return (
    <div className="ts-shell">
      {step === 'intro' && (
        <div className="ts-card">
          <x.h3 className="ts-h display" text={{ _: 'h3', md: 'h2' }}>
            Find yourself in the data.
          </x.h3>
          <p className="ts-sub">
            Answer two questions and we&rsquo;ll show you the data for homeowners like you from our 2026 survey of over
            1,000 Americans.
          </p>
          <DS.Button
            icon={DS.IconArrowRight}
            iconPosition="right"
            onClick={() => setStep('select')}
            size="md"
            variant="cta"
          >
            Show me my data
          </DS.Button>
        </div>
      )}

      {step === 'select' && (
        <div className="ts-card">
          <p className="ts-eyebrow">
            <span className="dot" />
            <span>Two quick picks</span>
          </p>
          <DS.Box mb={5}>
            <p className="ts-q-label">Which generation are you?</p>
            <DS.SegmentedControl<{ key: Generation; label: Generation }>
              fullWidth
              handleSegmentsSelected={(selected) => {
                if (selected[0]) setGen(selected[0].key);
              }}
              segments={GENERATIONS.map((g) => ({ key: g, label: g }))}
              selectedSegmentKeys={gen ? [gen] : []}
            />
          </DS.Box>
          <DS.Box mb={5}>
            <p className="ts-q-label">Where in the U.S. do you live?</p>
            <DS.SegmentedControl<{ key: Region; label: Region }>
              fullWidth
              handleSegmentsSelected={(selected) => {
                if (selected[0]) setRegion(selected[0].key);
              }}
              segments={REGIONS.map((r) => ({ key: r, label: r }))}
              selectedSegmentKeys={region ? [region] : []}
            />
          </DS.Box>
          <DS.Button disabled={!ready} fullWidth onClick={run} size="md" variant="cta">
            Show me my data
          </DS.Button>
        </div>
      )}

      {step === 'loading' && <Loading />}

      {step === 'results' && gen && region && <ToolResults gen={gen} onRestart={restart} region={region} />}
    </div>
  );
};

/* =================== CHARTS (Module 5) =================== */

type DataTableProps = {
  caption: string;
  head: string[];
  rows: string[][];
};

const DataTable = ({ caption, head, rows }: DataTableProps) => (
  <table className="dtable">
    <caption>{caption}</caption>
    <thead>
      <tr>
        {head.map((h) => (
          <th key={h}>{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.join('|')}>
          {r.map((c) => (
            <td key={c}>{c}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

/* =================== TICKER (Module 2) — continuous marquee =================== */

// Two visual copies for the seamless marquee; a third hidden copy below carries the same content to AT.
const TickerLoop = ({ loopKey }: { loopKey: string }) => (
  <>
    {TICKER.map((stat, idx) => (
      // eslint-disable-next-line react/no-array-index-key -- static, fixed-order list; index is a stable key
      <span className="ticker-pair" key={`${loopKey}-${idx}`}>
        <span className="ticker-tag">Latest signal</span>
        <span className="ticker-item">{stat}</span>
      </span>
    ))}
  </>
);

const Ticker = () => (
  <div aria-label="Latest signal stats" className="ticker" role="region">
    <div aria-hidden="true" className="ticker-track">
      <TickerLoop loopKey="a" />
      <TickerLoop loopKey="b" />
    </div>
    {/* Announce one copy to assistive tech — the visual track is marked aria-hidden. */}
    <ul className="ticker-a11y">
      {TICKER.map((stat, idx) => (
        // eslint-disable-next-line react/no-array-index-key -- static, fixed-order list; index is a stable key
        <li key={idx}>{stat}</li>
      ))}
    </ul>
  </div>
);

/* =================== FAQ (Module 10) =================== */

type FaqItemProps = { a: ReactNode; q: string };

const FaqItem = ({ a, q }: FaqItemProps) => (
  <DS.ProgressiveDisclosure
    trigger={
      <DS.Text as="span" fontWeight={600} text="body16">
        {q}
      </DS.Text>
    }
    // Questions wrap on narrow screens (see the trigger override in the CSS); grow to fit them
    triggerProps={{ h: 'auto', minHeight: 8 }}
  >
    <DS.Text color="uiHelperCopy" maxWidth="70ch" text="body16">
      {a}
    </DS.Text>
  </DS.ProgressiveDisclosure>
);

/* =================== PAGE =================== */

export const TrustSignal = () => (
  <div className="trust-signal-page">
    {/* MODULE 1 — HERO */}
    <section className="hero">
      <div className="wrap hero-grid">
        <div className="hero-text">
          <p className="eyebrow">The Aurora Solar Trust Signal 2026</p>
          <x.h1 className="display" mb={4} text={{ _: 'h2', md: 'h1' }}>
            Interested in solar but have some concerns? You are not alone, and we&rsquo;re keeping score.
          </x.h1>
          <p className="lead">Here&rsquo;s what homeowners actually think, and what you can do to get the best deal.</p>
          <DS.Button
            as="a"
            href="#best-deal"
            icon={DS.IconArrowDown}
            iconPosition="right"
            size="md"
            textDecoration={{ hover: 'none' }}
            variant="cta"
          >
            How to get the best deal
          </DS.Button>
        </div>
        <div className="hero-art">
          <Image
            alt="A solar installer reviewing system details on a tablet with two homeowners outside their solar-equipped home"
            className="hero-photo"
            fill
            priority
            sizes="(min-width: 860px) 50vw, 100vw"
            src="/home-solar/trust-signal/hero.webp"
          />
        </div>
      </div>
    </section>

    {/* MODULE 2 — TICKER */}
    <Ticker />

    {/* MODULE 2.5 — HOW TO GET THE BEST DEAL */}
    <section className="sec" id="best-deal">
      <div className="wrap">
        <x.h2 className="display" mb={3} text={{ _: 'h3', md: 'h2' }}>
          How to get the best deal
        </x.h2>
        <p className="sec-sub">
          Before you talk to anyone, here&rsquo;s what the homeowners who feel best about their decision look for, and
          how to find solar estimates you can trust.
        </p>
        <DS.Grid gap={4} gridTemplateColumns={{ _: '1fr', md: 'repeat(3, 1fr)' }} mt={5}>
          {BEST_DEAL_CARDS.map(({ body, href, icon: Icon, linkText, supIndex, title }) => (
            <DS.Card key={title}>
              <div className="best-deal-card">
                <Icon size={5} />
                <h3>{title}</h3>
                <p>
                  <span>{body}</span>
                  <span className="sup">{supIndex}</span>
                </p>
                <Link href={href}>
                  <span>{linkText} </span>
                  <DS.IconExternalLink size={3} />
                </Link>
              </div>
            </DS.Card>
          ))}
        </DS.Grid>
      </div>
    </section>

    {/* MODULE 3 — TOOL */}
    <section className="sec" id="tool">
      <div className="wrap">
        <Tool />
      </div>
    </section>

    {/* MODULE 5 — CHARTS */}
    <section className="sec" id="signal-trend">
      <div className="wrap">
        <p className="eyebrow">
          <span className="dot" />
          <span>The signal</span>
        </p>
        <x.h2 className="display" mb={3} text={{ _: 'h3', md: 'h2' }}>
          Solar companies are gaining trust.
        </x.h2>
        <p className="sec-sub">
          On the left, our quarterly Aurora Solar Trust Signal, the share of homeowners who didn&rsquo;t have a negative
          experience with a solar professional has positively risen across the last four seasons. Higher is better. On
          the right, the long-term trust view from the annual Aurora Solar Snapshot: homeowner mistrust spikes in 2024
          and improved since. The Aurora Solar Trust Signal is a direction measured the same way each season; the trend
          is the point.
        </p>

        <DS.Grid gap={4} gridTemplateColumns={{ _: '1fr', md: '1fr 1fr' }} mt={5}>
          <DS.Card>
            <div className="chart-card-content">
              <h3>Aurora Solar Trust Signal (quarterly)</h3>
              <p className="meta">
                <span>Didn&rsquo;t have a negative experience with a solar pro · </span>
                <span className="lower">Higher = better</span>
              </p>
              <LineChart
                ci={2.75}
                color="var(--maroon)"
                data={SIGNAL}
                label="Aurora Solar Trust Signal quarterly trend: share of homeowners without a negative solar experience"
                yMax={45}
              />
              <DataTable
                caption="Quarterly Aurora Solar Trust Signal (Dynata, n ≈ 1,000/wave; 95% CI ±2.5–3.0 pts)"
                head={['Wave', 'Didn’t have a negative experience']}
                rows={SIGNAL.map((d) => [d.label, `${d.v}%`])}
              />
            </div>
          </DS.Card>
          <DS.Card>
            <div className="chart-card-content">
              <h3>Aurora Solar Snapshot year-over-year mistrust</h3>
              <p className="meta">Long-term arc · annual study</p>
              <LineChart
                color="var(--chart-secondary)"
                dashed
                data={YOY}
                label="Aurora Solar Snapshot year-over-year homeowner mistrust"
                yMax={50}
              />
              <DataTable
                caption="Year-over-year mistrust (Aurora Solar Snapshot / Coleman Parkes & Dynata)"
                head={['Year', 'Mistrust']}
                rows={YOY.map((d) => [d.label, `${d.v}%`])}
              />
            </div>
          </DS.Card>
        </DS.Grid>

        <p className="caption-line">
          Share of all surveyed homeowners who did not report a negative experience with a solar professional, by wave.
          Aurora Solar Trust Signal: Aurora Solar / Dynata (n&nbsp;&asymp;&nbsp;1,000 per wave). Year-over-year
          mistrust: Aurora Solar Snapshot / Coleman Parkes &amp; Dynata.
        </p>
        <dl className="source-legend">
          <dt>
            <span className="sup">1</span>
          </dt>
          <dd>Aurora Solar Snapshot 2026 (Coleman Parkes, n&nbsp;=&nbsp;1,112).</dd>
          <dt>
            <span className="sup">2</span>
          </dt>
          <dd>Aurora Solar Trust Signal, 3-wave trend (Dynata, n&nbsp;&asymp;&nbsp;1,000 per wave).</dd>
        </dl>
      </div>
    </section>

    {/* MODULE 6 — TESTIMONIAL */}
    <section className="sec">
      <div className="wrap narrow">
        <div className="quote-card">
          <blockquote>
            &quot;[Utility] costs are rapidly rising, outpacing what consumption is for growing families out here,
            including ours. We wanted to be a little bit more in control of what we had in terms of our consumption and
            our footprint. So we decided that it would be a prudent endeavor to go down this [solar + storage]
            path.&quot;
          </blockquote>
          <div className="who">
            <div>
              <b>Kyle</b>
              <span>Arizona</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* MODULE 7 — ESTIMATE BANNER */}
    <DS.Box as="section" bg="uiDropdown.selectedIconColor" py={{ _: 9, md: 10 }} textAlign="center">
      <div className="wrap">
        <DS.Text
          as="h2"
          className="display"
          color="uiButtonPrimary.defaultTextColor"
          fontSize={{ _: 'h2', md: 'h1' }}
          mb={5}
        >
          Curious what solar actually looks like for your home?
        </DS.Text>
        <ButtonLink
          affiliateId={AffiliateId.MINITOOLS}
          event={{ data: { page: 'trust-signal' }, name: TRACKING.TRUST_SIGNAL_CLICKED_CTA }}
          icon={DS.IconArrowRight}
          iconPosition="right"
          path="/quote-builder"
          size="md"
          variant="cta"
        >
          Get a no-pressure estimate
        </ButtonLink>
      </div>
    </DS.Box>

    {/* MODULE 8 — METHODOLOGY */}
    <section className="sec" id="methodology">
      <div className="wrap narrow method">
        <p className="eyebrow">
          <span className="dot" />
          <span>Methodology</span>
        </p>
        <x.h2 className="display" mb={3} text={{ _: 'h3', md: 'h2' }}>
          How we measure the Aurora Solar Trust Signal.
        </x.h2>
        <p>Two studies power this page.</p>
        <h3 className="method-study">The Aurora Solar Trust Signal</h3>
        <p>
          A quarterly survey of about 1,000 U.S. homeowners, run with Dynata. We ask the same questions each season
          about homeowners&rsquo; real experiences with solar professionals. This is what powers the trend graph and our
          seasonal blog posts. Because we ask the same way every wave, we can read the direction of change over time,
          which is the whole point of a &ldquo;signal.&rdquo;
        </p>
        <h3 className="method-study">
          <a className="snapshot-link" href={SNAPSHOT_URL} rel="noopener noreferrer" target="_blank">
            The Aurora Solar Snapshot
          </a>
        </h3>
        <p>
          Our annual industry study, conducted by Coleman Parkes. The 2026 wave surveyed 1,112 U.S. homeowners (fielded
          February 2026). The Aurora Solar Snapshot powers the &ldquo;Find Yourself in the Data&rdquo; tool and the
          year-over-year context shown alongside the trend.
        </p>
        <p>
          <strong>What we claim, and what we don&rsquo;t.</strong>
          <span>
            {' '}
            We report the Aurora Solar Trust Signal as a direction, measured the same way each season, not as a precise
            absolute rate. Survey questions about personal experience can be answered in more than one way, so we
            don&rsquo;t anchor to a single headline percentage as a &ldquo;true&rdquo; level. Reported negative
            experiences declined across our last four waves; we report that trend, not a precise quarter-to-quarter
            number.
          </span>
        </p>
        <p>
          <strong>Notes on comparability.</strong>
          <span>
            {' '}
            Our Spring 2026 wave added an &ldquo;Other&rdquo; response option to two questions; the trend holds with or
            without it. The wording for &ldquo;not approached by a solar professional&rdquo; varied slightly across
            waves. In the tool, figures are unweighted and match our published Aurora Solar Snapshot benchmarks; for
            small subgroups the tool shows the closest reliable match rather than a number built on only a handful of
            responses.
          </span>
        </p>
        <p className="note">Survey dates: Fall 2025, Winter 2026, Spring 2026, Summer 2026.</p>
      </div>
    </section>

    {/* MODULE 9 — REPORTER BANNER */}
    <DS.Box as="section" bg="uiButtonPrimary.defaultBgColor" py={{ _: 9, md: 10 }} textAlign="center">
      <div className="wrap">
        <DS.Text
          as="h2"
          className="display"
          color="uiButtonPrimary.defaultTextColor"
          fontSize={{ _: 'h2', md: 'h1' }}
          mb={4}
        >
          Reporter or analyst? Get the full industry data.
        </DS.Text>
        <DS.Text color="uiButtonPrimary.defaultTextColor" maxWidth="60ch" mb={6} mt={3} mx="auto" text="body16">
          The Aurora Solar Trust Signal is the consumer companion to the Aurora Solar Snapshot, our annual industry
          study: full dataset, methodology, and media assets.
        </DS.Text>
        <DS.Button
          as="a"
          href={SNAPSHOT_URL}
          icon={DS.IconArrowRight}
          iconPosition="right"
          rel="noopener noreferrer"
          size="md"
          target="_blank"
          textDecoration={{ hover: 'none' }}
          variant="cta"
        >
          View the industry data
        </DS.Button>
      </div>
    </DS.Box>

    {/* MODULE 10 — FAQ */}
    <section className="sec">
      <div className="wrap narrow">
        <p className="eyebrow">
          <span className="dot" />
          <span>FAQ</span>
        </p>
        <x.h2 className="display" mb={6} text={{ _: 'h3', md: 'h2' }}>
          About the data
        </x.h2>
        <DS.Flex gap={5}>
          {FAQ_DATA.map((entry) => (
            <FaqItem a={renderFaqAnswer(entry)} key={entry.q} q={entry.q} />
          ))}
        </DS.Flex>
      </div>
    </section>
  </div>
);
